import { AdminCatalog, AdminCatalogProduct } from './pages/AdminCatalog.tsx';
import { PrescriptionsPage } from './pages/Prescriptions.tsx';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HttpError } from './api.ts';
import { AuthGate } from './components/AuthGate.tsx';
import { Shell } from './components/Shell.tsx';
import { Login } from './pages/Login.tsx';
import { KitsPage } from './pages/Kits.tsx';
import { CatalogOptinPage } from './pages/CatalogOptin.tsx';
import { QuestionsPage } from './pages/Questions.tsx';
import { MessagesPage } from './pages/Messages.tsx';
import { ClaimsPage } from './pages/Claims.tsx';
import { PrivacyPage } from './pages/Privacy.tsx';
import { ForgotPassword, ResetPassword, Signup, VerifyEmail } from './pages/Public.tsx';
import { CompanyPage } from './pages/Company.tsx';
import { AttributesPage, TaxRulesPage } from './pages/CatalogSettings.tsx';
import { ApiKeysPage } from './pages/ApiKeys.tsx';
import { OrdersPage } from './pages/Orders.tsx';
import { StoresPage } from './pages/Stores.tsx';
import { AdminGate, AdminOverview, AdminTenant, AdminTenants } from './pages/Admin.tsx';
import { Catalog } from './pages/Catalog.tsx';
import { ProductPage } from './pages/Product.tsx';
import { Imports } from './pages/Imports.tsx';
import { MonitorPage } from './pages/Monitor.tsx';
import { SalesPage } from './pages/Sales.tsx';
import { FinanceExpenses, FinancePayouts, FinanceReconciliation, FinanceResults } from './pages/Finance.tsx';
import { ComingSoon } from './pages/ComingSoon.tsx';
import { AdminServer } from './pages/AdminServer.tsx';
import { AdminCompliance } from './pages/AdminCompliance.tsx';
import { AdminSources } from './pages/AdminSources.tsx';
import { General } from './pages/General.tsx';
import { GoalsPage } from './pages/Goals.tsx';
import { PharmacyPage } from './pages/Pharmacy.tsx';
import { BrandAuthorizations } from './pages/BrandAuthorizations.tsx';
import { ChannelHealth } from './pages/ChannelHealth.tsx';
import { ShopeeBuybox } from './pages/ShopeeBuybox.tsx';
import { ShopeeCatalog } from './pages/ShopeeCatalog.tsx';
import { AdminBrands } from './pages/AdminBrands.tsx';
import { AdminDocuments } from './pages/AdminDocuments.tsx';
import { AdminContests } from './pages/AdminContests.tsx';
import { AdminNewItems } from './pages/AdminNewItems.tsx';
import { AdminDivergences } from './pages/AdminDivergences.tsx';
import { AdminNoLeaflet } from './pages/AdminNoLeaflet.tsx';
import { AdminMasterCandidates } from './pages/AdminMasterCandidates.tsx';
import { AdminSanitary } from './pages/AdminSanitary.tsx';
import { GeneralSettingsPage } from './pages/GeneralSettings.tsx';
import { Channels } from './pages/Channels.tsx';
import { MeliCallback } from './pages/MeliCallback.tsx';
import { ShopeeCallback } from './pages/ShopeeCallback.tsx';
import { UsersPage } from './pages/Users.tsx';
import { IfoodSummary } from './pages/IfoodChannel.tsx';
import { ShopeeListings, ShopeePublications, ShopeePublish, ShopeeSummary } from './pages/ShopeeChannel.tsx';
import { MeliListings, MeliProducts, MeliSummary } from './pages/MeliChannel.tsx';
import { Opportunities } from './pages/Opportunities.tsx';
import { Competitors } from './pages/Competitors.tsx';
import { DeliveriesPage } from './pages/Deliveries.tsx';
import { ListingShippingPage } from './pages/ListingShipping.tsx';
import { Prices } from './pages/Prices.tsx';
import { Publications } from './pages/Publications.tsx';
import { ChannelSettingsPage } from './pages/ChannelSettings.tsx';
import { OwnListingsPage } from './pages/OwnListings.tsx';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => !(err instanceof HttpError && err.status < 500) && count < 2,
    },
  },
});

// Instalação como app (PWA). Só em produção: em desenvolvimento o cache atrapalharia.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<Signup />} />
          <Route path="/privacidade" element={<PrivacyPage />} />
          <Route path="/confirmar-email" element={<VerifyEmail />} />
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />
          <Route element={<AuthGate />}>
            <Route element={<Shell />}>
              <Route index element={<General />} />
              <Route path="configuracoes/metas" element={<GoalsPage />} />
              <Route path="configuracoes/farmacia" element={<PharmacyPage />} />
              <Route path="configuracoes/autorizacoes-marca" element={<BrandAuthorizations />} />
              <Route path="catalogo" element={<Catalog />} />
              <Route path="catalogo/kits" element={<KitsPage />} />
              <Route path="catalogo/:id" element={<ProductPage />} />
              <Route path="importacoes" element={<Imports />} />
              <Route path="canais" element={<Channels />} />
              <Route path="empresa" element={<CompanyPage />} />
              <Route path="configuracoes/atributos" element={<AttributesPage />} />
              <Route path="configuracoes/regras-tributarias" element={<TaxRulesPage />} />
              <Route path="configuracoes/api" element={<ApiKeysPage />} />
              <Route path="configuracoes/usuarios" element={<UsersPage />} />
              <Route path="pedidos" element={<OrdersPage />} />
              <Route path="fila" element={<MonitorPage />} />
              <Route path="vendas" element={<SalesPage />} />
              <Route path="financeiro/conciliacao" element={<FinanceReconciliation />} />
              <Route path="vendas/resultados" element={<FinanceResults />} />
              <Route path="financeiro/resultados" element={<Navigate to="/vendas/resultados" replace />} />
              <Route path="financeiro/repasses" element={<FinancePayouts />} />
              <Route path="financeiro/despesas" element={<FinanceExpenses />} />
              <Route path="canal/mercadolivre/receitas" element={<PrescriptionsPage />} />
              <Route path="canal/mercadolivre/entregas" element={<DeliveriesPage />} />
              <Route path="canal/mercadolivre/envio" element={<ListingShippingPage />} />
              <Route path="canal/mercadolivre/entrar-catalogo" element={<CatalogOptinPage />} />
              <Route path="canal/mercadolivre/perguntas" element={<QuestionsPage />} />
              <Route path="canal/mercadolivre/mensagens" element={<MessagesPage />} />
              <Route path="canal/mercadolivre/reclamacoes" element={<ClaimsPage />} />
              <Route path="configuracoes/lojas" element={<StoresPage />} />
              <Route path="configuracoes/gerais" element={<GeneralSettingsPage />} />
              <Route path="app/meli" element={<MeliCallback />} />
              <Route path="app/shopee" element={<ShopeeCallback />} />
              <Route path="canal/ifood" element={<IfoodSummary />} />
              <Route path="canal/ifood/precos" element={<Prices key="ifood" />} />
              <Route path="canal/ifood/configuracoes" element={<ChannelSettingsPage key="ifood" />} />
              <Route path="canal/shopee" element={<ShopeeSummary />} />
              <Route path="canal/shopee/saude" element={<ChannelHealth channel="shopee" />} />
              <Route path="canal/shopee/buybox" element={<ShopeeBuybox />} />
              <Route path="canal/shopee/entrar-catalogo" element={<ShopeeCatalog />} />
              <Route path="canal/shopee/anuncios" element={<ShopeeListings />} />
              <Route path="canal/shopee/publicar" element={<ShopeePublish />} />
              <Route path="canal/shopee/categorias" element={<Navigate to="/canal/shopee/publicar" replace />} />
              <Route path="canal/shopee/publicacoes" element={<ShopeePublications />} />
              <Route path="canal/shopee/precos" element={<Prices key="shopee" />} />
              <Route path="canal/shopee/configuracoes" element={<ChannelSettingsPage key="shopee" />} />
              <Route path="canal/mercadolivre" element={<MeliSummary />} />
              <Route path="canal/mercadolivre/produtos" element={<MeliProducts />} />
              <Route path="canal/mercadolivre/anuncios" element={<MeliListings key="all" />} />
              <Route path="canal/mercadolivre/oportunidades" element={<Opportunities />} />
              <Route path="canal/mercadolivre/concorrentes" element={<Competitors />} />
              <Route path="canal/mercadolivre/precos" element={<Prices />} />
              <Route path="canal/mercadolivre/publicacoes" element={<Publications />} />
              <Route path="canal/mercadolivre/sem-catalogo" element={<OwnListingsPage />} />
              <Route path="canal/mercadolivre/configuracoes" element={<ChannelSettingsPage />} />
              <Route path="canal/mercadolivre/conciliar" element={<MeliListings key="review" review />} />
              <Route path="admin" element={<AdminGate />}>
                <Route index element={<AdminOverview />} />
                <Route path="clientes" element={<AdminTenants />} />
                <Route path="clientes/:id" element={<AdminTenant />} />
                <Route path="catalogo" element={<AdminCatalog />} />
                <Route path="servidor" element={<AdminServer />} />
                <Route path="compliance" element={<AdminCompliance />} />
                <Route path="fontes" element={<AdminSources />} />
                <Route path="documentos" element={<AdminDocuments />} />
                <Route path="contestacoes" element={<AdminContests />} />
                <Route path="itens-novos" element={<AdminNewItems />} />
                <Route path="divergencias" element={<AdminDivergences />} />
                <Route path="sem-bula" element={<AdminNoLeaflet />} />
                <Route path="candidatos" element={<AdminMasterCandidates />} />
                <Route path="acoes-sanitarias" element={<AdminSanitary />} />
                <Route path="restricoes-marca" element={<AdminBrands />} />
                <Route path="catalogo/:id" element={<AdminCatalogProduct />} />
              </Route>
              <Route path="em-breve/:area" element={<ComingSoon />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
